import { useState, useCallback, useRef } from "react";
import { deleteSchedulerEventRange } from "repositories/scheduler-repository";
import { handleError, handleSuccess } from "utils";
import {
  buildDeleteRangePayload,
  dedupeClearableItems,
  getClearableItemDedupeKey,
  getClearItemCounts,
  resolveDeleteRangeIsShift,
} from "./eventsWorkspaceClear.utils";

const areClearableListsEqual = (left = [], right = []) => {
  if (left.length !== right.length) return false;
  const leftKeys = left
    .map((item) => getClearableItemDedupeKey(item))
    .filter(Boolean)
    .sort()
    .join(",");
  const rightKeys = right
    .map((item) => getClearableItemDedupeKey(item))
    .filter(Boolean)
    .sort()
    .join(",");
  return leftKeys === rightKeys;
};

/**
 * Global Clear Day/Week/Month flow for Event Workspace.
 * Uses SchedulerEvent/DeleteRange so only occurrences in the period are removed.
 * @param {{
 *   t: Function,
 *   calendarViewId: string,
 *   currentDate: Date,
 *   ownerId: number|null|undefined,
 *   eventShiftFilter?: string[],
 *   officeIds?: number[],
 *   userIds?: number[],
 *   onRefresh?: () => void,
 *   onClearSelection?: () => void,
 * }} options
 */
export function useEventsWorkspaceClear({
  t,
  calendarViewId,
  currentDate,
  ownerId,
  eventShiftFilter = [],
  officeIds = [],
  userIds = [],
  onRefresh,
  onClearSelection,
}) {
  const [visibleClearableItems, setVisibleClearableItems] = useState([]);
  const [confirmModal, setConfirmModal] = useState(null);
  const [isClearing, setIsClearing] = useState(false);
  const isClearingRef = useRef(false);

  const handleVisibleClearableItemsUpdate = useCallback((items = []) => {
    const normalized = dedupeClearableItems(items);
    setVisibleClearableItems((prev) =>
      areClearableListsEqual(prev, normalized) ? prev : normalized,
    );
  }, []);

  const canClearPeriod = visibleClearableItems.length > 0 && !isClearing;

  const closeConfirmModal = useCallback(() => {
    if (isClearingRef.current) return;
    setConfirmModal(null);
  }, []);

  const executeClear = useCallback(async () => {
    if (!ownerId || isClearingRef.current || !confirmModal) return;

    const itemsToClear = confirmModal.items || visibleClearableItems;
    isClearingRef.current = true;
    setIsClearing(true);
    try {
      const payload = buildDeleteRangePayload({
        ownerId,
        calendarViewId,
        currentDate,
        clearableItems: itemsToClear,
        eventShiftFilter,
        officeIds,
        userIds,
      });
      // Use IsShift snapshotted when the confirm modal opened (matches modal counts).
      payload.IsShift = confirmModal.isShift;
      await deleteSchedulerEventRange(payload);
      handleSuccess(t("scheduler.clearPeriodSuccess"));
      setConfirmModal(null);
      onClearSelection?.();
      onRefresh?.();
    } catch (error) {
      handleError(error?.message || error);
    } finally {
      isClearingRef.current = false;
      setIsClearing(false);
    }
  }, [
    ownerId,
    confirmModal,
    visibleClearableItems,
    calendarViewId,
    currentDate,
    eventShiftFilter,
    officeIds,
    userIds,
    t,
    onClearSelection,
    onRefresh,
  ]);

  const openClearPeriodConfirm = useCallback(() => {
    if (!ownerId || isClearingRef.current) return;
    if (!visibleClearableItems.length) return;

    const counts = getClearItemCounts(visibleClearableItems);
    setConfirmModal({
      items: visibleClearableItems,
      isShift: resolveDeleteRangeIsShift({
        clearableItems: visibleClearableItems,
        eventShiftFilter,
      }),
      ...counts,
    });
  }, [ownerId, visibleClearableItems, eventShiftFilter]);

  const confirmClear = useCallback(() => {
    if (!confirmModal || isClearingRef.current) return;
    executeClear();
  }, [confirmModal, executeClear]);

  return {
    visibleClearableItems,
    canClearPeriod,
    isClearing,
    confirmModal,
    handleVisibleClearableItemsUpdate,
    openClearPeriodConfirm,
    closeConfirmModal,
    confirmClear,
  };
}
